import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Activity, ShieldCheck } from "lucide-react";

const PHOTOS = [
  { src: "/img/production/prod-still-1.webp", alt: "Overview of the SunPress assembly line", label: "Assembly line", sub: "Hand-built, one station at a time" },
  { src: "/img/production/prod-still-2.webp", alt: "Electrical test stations checking every unit", label: "Electrical & heat testing", sub: "12-station QA rig on every unit" },
  { src: "/img/production/prod-still-3.webp", alt: "Finished ceramic soleplates racked for inspection", label: "Heating element & soleplate", sub: "Inner element and plate verified for even heat" },
  { src: "/img/production/prod-photo-1.webp", alt: "CORE 500 irons on the automated quality-testing rig", label: "Temperature & drop checks", sub: "Thermostat tolerance and casing checked" },
  { src: "/img/production/prod-photo-2.webp", alt: "Freshly packed CORE 500 irons and retail boxes on the packing belt", label: "Packaging", sub: "Wrapped, serial-logged and boxed" },
  { src: "/img/production/prod-still-4.webp", alt: "Boxing station preparing retail cartons", label: "Dispatch", sub: "Sealed and stacked for Nigeria-wide delivery" },
];

const BADGES = [
  { icon: Activity, label: "Tested for African Inverter Systems" },
  { icon: Zap, label: "Engineered Low-Surge Startup" },
  { icon: ShieldCheck, label: "1-Year Warranty Included", to: "/warranty" },
];

const Badge = ({ b }) => {
  const I = b.icon;
  const inner = (
    <>
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0" style={{ background: "var(--ink)" }}>
        <I size={17} style={{ color: "var(--sun-amber)" }} strokeWidth={2} />
      </span>
      <span className="text-sm font-medium leading-tight" style={{ color: "var(--ink)" }}>{b.label}</span>
    </>
  );
  const cls = "flex items-center gap-3 px-5 py-4 rounded-2xl";
  const style = { background: "var(--surface)", border: "1px solid var(--border)" };
  return b.to ? (
    <Link to={b.to} className={`${cls} hover:opacity-80 transition-opacity`} style={style} data-testid={`proof-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{inner}</Link>
  ) : (
    <div className={cls} style={style} data-testid={`proof-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{inner}</div>
  );
};

export default function ProductionProofSection() {
  return (
    <section data-testid="production-proof" className="py-20 md:py-28" style={{ background: "var(--bg)" }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow">Factory &amp; production proof</div>
            <h2 className="display mt-4" style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)", color: "var(--ink)" }}>
              Engineered for Reliability — <span className="sunset-text">Inside Our Production</span>.
            </h2>
          </div>
          <div className="md:col-span-5 text-base" style={{ color: "var(--ink-soft)" }}>
            Real photos from the SunPress line — wiring and heating elements, assembly, QA testing and
            packaging. Nothing staged.
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {PHOTOS.map((p, i) => (
            <figure key={p.src} data-testid={`proof-photo-${i}`} className="relative overflow-hidden rounded-2xl glossy" style={{ aspectRatio: "4/3" }}>
              <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4" style={{ background: "linear-gradient(180deg, transparent, rgba(20,16,12,0.85))" }}>
                <div className="text-sm font-medium" style={{ color: "#FCF6EA" }}>{p.label}</div>
                <div className="text-xs mt-0.5 hidden sm:block" style={{ color: "rgba(252,246,234,0.72)" }}>{p.sub}</div>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="proof-badges">
          {BADGES.map((b) => <Badge key={b.label} b={b} />)}
        </div>

        <Link to="/production" data-testid="proof-album-link" className="mt-10 inline-flex items-center gap-2 text-sm font-medium sunset-text py-2">
          Open the full photo &amp; video album <ArrowRight size={14} color="var(--sun-orange)" />
        </Link>
      </div>
    </section>
  );
}
